import { Select } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { statusDictionary } from '@/Ship/Components/Filters/StatusFilterBar/StatusDictionaryStore'

import type { FieldMethods } from '@/Core/UI/Form/types'

interface Props {
    /**
     * ⚠️ `FieldMethods` ядра описує лише методи, а в рантаймі поруч лежить і
     * визначення поля — з нього беремо ключ довідника.
     */
    field?: FieldMethods & { definition?: { statusResource?: unknown } }
    index?: number
}

/**
 * Поле «Статус» у панелі фільтрів реєстру, де статус — рядок довідника
 * (клієнти, перевізники, справи).
 *
 * Значення — **код** статусу, той самий, що пише рядок статусів над таблицею
 * (`StatusFilterBar`): у списку один стан — `filter.status`, і вибір у панелі
 * підсвічує плашку, а клік по плашці показується тут.
 *
 * Ядрова панель перетворює поле `status` на звичайний select і чекає варіантів
 * у ресурсі. Варіанти довідника змінює адміністратор, тож у ресурс їх не
 * впишеш — це поле бере їх із того самого `statusDictionary`, що й рядок.
 * Підключається у ресурсі: `filter.fieldProps.status.input`.
 */
export const StatusDictionarySelect = observer(({ field, index }: Props) => {
    const resource = field?.definition?.statusResource
    const dictionary = typeof resource === 'string' && resource !== '' ? statusDictionary(resource) : null

    useEffect(() => {
        if (dictionary && !dictionary.isLoaded) void dictionary.refresh()
    }, [dictionary])

    if (!field) return null

    const { value, onChange } = field.bind({ index: index ?? 0 })
    const code = typeof value === 'string' && value !== '' ? value : undefined

    return (
        <Select
            allowClear
            showSearch
            style={{ width: '100%' }}
            value={code}
            loading={dictionary !== null && !dictionary.isLoaded}
            options={(dictionary?.options ?? []).map((option) => ({ value: option.value, label: option.label }))}
            optionFilterProp="label"
            // Очищення — `undefined`: ключ фільтра зникає, а не стає порожнім рядком
            onChange={(next: string | undefined) => onChange(next ?? undefined)}
        />
    )
})
